import { useEffect, useMemo, useState } from "react";
import { format, differenceInCalendarDays } from "date-fns";
import { fr as frLocale, enUS, arSA } from "date-fns/locale";
import { CalendarIcon, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { villas, PRICING } from "@/data/villas";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const LOCALES = { fr: frLocale, en: enUS, ar: arSA };

function estimatePrice(range) {
  if (!range?.from || !range?.to) return null;
  const nights = differenceInCalendarDays(range.to, range.from);
  if (nights < 1) return null;
  const m = range.from.getMonth() + 1;
  const bucket = m >= 4 && m <= 10 ? "high" : "low";
  const rate = PRICING.hasna[bucket];
  return { nights, subtotal: rate * nights, rate, bucket };
}

export default function BookingForm() {
  const { t, lang, isRTL } = useLang();
  const villa = "hasna";
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [hasnaChoice, setHasnaChoice] = useState("entire");
  const [guests, setGuests] = useState(2);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [blocked, setBlocked] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api
      .get(`/villas/hasna/availability?suite=${hasnaChoice}`)
      .then((r) => {
        if (!cancelled) setBlocked(r.data.blocked_dates.map((d) => new Date(d)));
      })
      .catch(() => setBlocked([]));
    return () => { cancelled = true; };
  }, [hasnaChoice]);

  const est = useMemo(() => estimatePrice(range), [range]);
  const locale = LOCALES[lang] || frLocale;

  const disabledDates = [{ before: new Date() }, ...blocked];

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) {
      toast.error(t.booking.pickDates);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        villa,
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        guests: Number(guests),
        formula: "standard",
        hasna_choice: hasnaChoice,
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        message,
        language: lang,
      });
      toast.success(t.booking.success);
      setFirstName(""); setLastName(""); setEmail(""); setPhone(""); setMessage("");
      setRange({ from: undefined, to: undefined });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.booking.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="booking-form" className="grid lg:grid-cols-3 gap-8 lg:gap-12">
      <div className="lg:col-span-2 space-y-6">
        <div className="space-y-2">
          <Label className="dh-overline">{t.booking.hasnaChoiceLabel}</Label>
          <Select value={hasnaChoice} onValueChange={setHasnaChoice}>
            <SelectTrigger data-testid="booking-hasna-choice" className="h-12 rounded-none border-[color:hsl(var(--dh-ocre)/0.4)] bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="entire">{t.booking.hasnaOptions.entire}</SelectItem>
              <SelectItem value="master">{t.booking.hasnaOptions.master}</SelectItem>
              <SelectItem value="confort">{t.booking.hasnaOptions.confort}</SelectItem>
              <SelectItem value="twin">{t.booking.hasnaOptions.twin}</SelectItem>
              <SelectItem value="undecided">{t.booking.hasnaOptions.undecided}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-[color:hsl(var(--dh-muted))]">{t.booking.hasnaChoiceHelp}</p>
        </div>

        <div className="space-y-2">
          <Label className="dh-overline">{t.booking.checkIn} → {t.booking.checkOut}</Label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="booking-dates"
                className="w-full h-12 flex items-center gap-3 px-4 border border-[color:hsl(var(--dh-ocre)/0.4)] bg-white text-start text-sm"
              >
                <CalendarIcon className="h-4 w-4 text-[color:hsl(var(--dh-terracotta))]" />
                {range?.from ? (
                  range.to ? (
                    <span>{format(range.from, "PPP", { locale })} — {format(range.to, "PPP", { locale })}</span>
                  ) : (
                    <span>{format(range.from, "PPP", { locale })} — …</span>
                  )
                ) : (
                  <span className="text-[color:hsl(var(--dh-muted))]">{t.booking.pickDates}</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 w-auto">
              <Calendar
                mode="range"
                selected={range}
                onSelect={setRange}
                numberOfMonths={2}
                disabled={disabledDates}
                locale={locale}
                dir={isRTL ? "rtl" : "ltr"}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label className="dh-overline">{t.booking.guests}</Label>
            <Input
              data-testid="booking-guests"
              type="number" min={1} max={6}
              value={guests}
              onChange={(e) => setGuests(e.target.value)}
              className="h-12 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]"
            />
          </div>
          <div className="space-y-2">
            <Label className="dh-overline">{t.booking.phone}</Label>
            <Input data-testid="booking-phone" required value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
          </div>
          <div className="space-y-2">
            <Label className="dh-overline">{t.booking.firstName}</Label>
            <Input data-testid="booking-firstname" required value={firstName} onChange={(e) => setFirstName(e.target.value)} className="h-12 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
          </div>
          <div className="space-y-2">
            <Label className="dh-overline">{t.booking.lastName}</Label>
            <Input data-testid="booking-lastname" required value={lastName} onChange={(e) => setLastName(e.target.value)} className="h-12 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="dh-overline">{t.booking.email}</Label>
          <Input data-testid="booking-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
        </div>

        <div className="space-y-2">
          <Label className="dh-overline">{t.booking.message}</Label>
          <Textarea data-testid="booking-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-none bg-white border-[color:hsl(var(--dh-ocre)/0.4)]" />
        </div>
      </div>

      <aside className="lg:col-span-1">
        <div className="dh-card p-8 sticky top-28 space-y-5">
          <div>
            <div className="dh-overline">{t.booking.overline}</div>
            <div className="font-serif text-2xl mt-1">{villas.hasna.name}</div>
          </div>
          <div className="text-sm text-[color:hsl(var(--dh-muted))]">
            {t.booking.hasnaOptions[hasnaChoice] || t.booking.hasnaOptions.entire}
          </div>
          {est ? (
            <div className="space-y-2 pt-4 border-t border-[color:hsl(var(--dh-ocre)/0.2)]">
              <div className="flex justify-between text-sm">
                <span>{est.nights} {t.booking.nights} × ~{est.rate}€</span>
                <span className="font-medium">~{est.subtotal}€</span>
              </div>
              <div className="text-xs text-[color:hsl(var(--dh-muted))]">{t.pricing.touristTax}</div>
            </div>
          ) : (
            <div className="text-xs text-[color:hsl(var(--dh-muted))] pt-4 border-t border-[color:hsl(var(--dh-ocre)/0.2)]">{t.booking.subtitle}</div>
          )}
          <button
            type="submit"
            data-testid="booking-submit"
            disabled={submitting}
            className="dh-btn-primary w-full py-4 disabled:opacity-60"
          >
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> {t.booking.submitting}</> : t.booking.submit}
          </button>
        </div>
      </aside>
    </form>
  );
}
